package fr.manyverse.app.presentation.screens

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeContentPadding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.Logout
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.AutoAwesome
import androidx.compose.material.icons.filled.BugReport
import androidx.compose.material.icons.filled.DeleteForever
import androidx.compose.material.icons.filled.Lightbulb
import androidx.compose.material.icons.filled.MoreVert
import androidx.compose.material.icons.filled.People
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CenterAlignedTopAppBar
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.FilledIconButton
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.IconButtonDefaults
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Scaffold
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.TextRange
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardCapitalization
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.TextFieldValue
import androidx.compose.ui.unit.dp
import fr.manyverse.app.domain.model.GameTableInfo
import fr.manyverse.app.domain.model.User
import fr.manyverse.app.presentation.components.ErrorState
import fr.manyverse.app.presentation.components.welcome.ActiveTableCard
import fr.manyverse.app.presentation.layout.WindowSizeContainer
import fr.manyverse.app.presentation.layout.WindowWidth
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.active_tables
import manyverse.shared.generated.resources.active_tables_empty
import manyverse.shared.generated.resources.cancel
import manyverse.shared.generated.resources.character_count
import manyverse.shared.generated.resources.create_character
import manyverse.shared.generated.resources.create_table
import manyverse.shared.generated.resources.delete_account
import manyverse.shared.generated.resources.delete_account_confirm
import manyverse.shared.generated.resources.delete_account_loading
import manyverse.shared.generated.resources.delete_account_message
import manyverse.shared.generated.resources.delete_account_title
import manyverse.shared.generated.resources.error_loading_tables
import manyverse.shared.generated.resources.feedback_report_bug
import manyverse.shared.generated.resources.feedback_suggestion
import manyverse.shared.generated.resources.feedback_thank_you
import manyverse.shared.generated.resources.home
import manyverse.shared.generated.resources.join_table
import manyverse.shared.generated.resources.more_options
import manyverse.shared.generated.resources.my_universes
import manyverse.shared.generated.resources.sign_out
import manyverse.shared.generated.resources.table_code
import org.jetbrains.compose.resources.stringResource


@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun WelcomeScreen(
    user: User,
    onSignOut: () -> Unit,
    onDeleteAccount: () -> Unit,
    onNavigateToCharacters: () -> Unit,
    onCreateCharacter: () -> Unit,
    onNavigateToUniverses: () -> Unit,
    onCreateGame: () -> Unit,
    onJoinGame: (code: String) -> Unit,
    joinError: String? = null,
    isJoining: Boolean = false,
    isLoading: Boolean = false,
    isLoadingTables: Boolean = false,
    tablesError: String? = null,
    onRetryTables: () -> Unit = {},
    characterCount: Int = 0,
    activeTables: List<GameTableInfo> = emptyList(),
    onTableClick: (String, String) -> Unit = { _, _ -> },
    onReportBug: () -> Unit = {},
    onMakeSuggestion: () -> Unit = {},
    showFeedbackThankYou: Boolean = false,
    onFeedbackThankYouShown: () -> Unit = {},
    modifier: Modifier = Modifier
) {
    var menuExpanded by remember { mutableStateOf(false) }
    var showDeleteAccountDialog by remember { mutableStateOf(false) }
    val snackbarHostState = remember { SnackbarHostState() }
    val thankYouMessage = stringResource(Res.string.feedback_thank_you)

    LaunchedEffect(showFeedbackThankYou) {
        if (showFeedbackThankYou) {
            snackbarHostState.showSnackbar(thankYouMessage)
            onFeedbackThankYouShown()
        }
    }

    Scaffold(
        snackbarHost = { SnackbarHost(snackbarHostState) },
        topBar = {
            CenterAlignedTopAppBar(
                title = {
                    Text(
                        text = stringResource(Res.string.home),
                        style = MaterialTheme.typography.headlineMedium,
                        color = MaterialTheme.colorScheme.primary,
                        modifier = Modifier.testTag("welcomeMessage")
                    )
                },
                actions = {
                    Box {
                        IconButton(
                            onClick = { menuExpanded = true },
                            modifier = Modifier.testTag("welcomeMenuButton")
                        ) {
                            Icon(
                                imageVector = Icons.Default.MoreVert,
                                contentDescription = stringResource(Res.string.more_options)
                            )
                        }

                        DropdownMenu(
                            expanded = menuExpanded,
                            onDismissRequest = { menuExpanded = false }
                        ) {
                            DropdownMenuItem(
                                text = { Text(stringResource(Res.string.feedback_report_bug)) },
                                onClick = {
                                    menuExpanded = false
                                    onReportBug()
                                },
                                leadingIcon = {
                                    Icon(
                                        imageVector = Icons.Default.BugReport,
                                        contentDescription = null
                                    )
                                },
                                modifier = Modifier.testTag("reportBugButton")
                            )

                            DropdownMenuItem(
                                text = { Text(stringResource(Res.string.feedback_suggestion)) },
                                onClick = {
                                    menuExpanded = false
                                    onMakeSuggestion()
                                },
                                leadingIcon = {
                                    Icon(
                                        imageVector = Icons.Default.Lightbulb,
                                        contentDescription = null
                                    )
                                },
                                modifier = Modifier.testTag("makeSuggestionButton")
                            )

                            HorizontalDivider()

                            DropdownMenuItem(
                                text = { Text(stringResource(Res.string.sign_out)) },
                                onClick = {
                                    menuExpanded = false
                                    onSignOut()
                                },
                                leadingIcon = {
                                    Icon(
                                        imageVector = Icons.AutoMirrored.Filled.Logout,
                                        contentDescription = null,
                                        tint = MaterialTheme.colorScheme.error
                                    )
                                },
                                modifier = Modifier.testTag("signOutButton")
                            )

                            DropdownMenuItem(
                                text = { Text(stringResource(Res.string.delete_account)) },
                                onClick = {
                                    menuExpanded = false
                                    showDeleteAccountDialog = true
                                },
                                leadingIcon = {
                                    Icon(
                                        imageVector = Icons.Default.DeleteForever,
                                        contentDescription = null,
                                        tint = MaterialTheme.colorScheme.error
                                    )
                                }
                            )
                        }
                    }
                }
            )
        }
    ) { paddingValues ->
        Surface(
            modifier = modifier.fillMaxSize(),
            color = MaterialTheme.colorScheme.background
        ) {
            WindowSizeContainer(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(paddingValues)
                    .safeContentPadding()
            ) { windowWidth ->
                when (windowWidth) {
                    WindowWidth.COMPACT -> PortraitLayout(
                        characterCount = characterCount,
                        activeTables = activeTables,
                        isLoadingTables = isLoadingTables,
                        tablesError = tablesError,
                        onRetryTables = onRetryTables,
                        onNavigateToCharacters = onNavigateToCharacters,
                        onCreateCharacter = onCreateCharacter,
                        onNavigateToUniverses = onNavigateToUniverses,
                        onCreateGame = onCreateGame,
                        onJoinGame = onJoinGame,
                        joinError = joinError,
                        isJoining = isJoining,
                        onTableClick = onTableClick
                    )

                    WindowWidth.MEDIUM, WindowWidth.EXPANDED -> LandscapeLayout(
                        characterCount = characterCount,
                        activeTables = activeTables,
                        isLoadingTables = isLoadingTables,
                        tablesError = tablesError,
                        onRetryTables = onRetryTables,
                        onNavigateToCharacters = onNavigateToCharacters,
                        onCreateCharacter = onCreateCharacter,
                        onNavigateToUniverses = onNavigateToUniverses,
                        onCreateGame = onCreateGame,
                        onJoinGame = onJoinGame,
                        joinError = joinError,
                        isJoining = isJoining,
                        onTableClick = onTableClick
                    )
                }
            }
        }
    }

    if (showDeleteAccountDialog) {
        AlertDialog(
            onDismissRequest = { if (!isLoading) showDeleteAccountDialog = false },
            title = { Text(stringResource(Res.string.delete_account_title)) },
            text = {
                if (isLoading) {
                    Column(
                        horizontalAlignment = Alignment.CenterHorizontally,
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        CircularProgressIndicator()
                        Spacer(Modifier.height(8.dp))
                        Text(stringResource(Res.string.delete_account_loading))
                    }
                } else {
                    Text(stringResource(Res.string.delete_account_message))
                }
            },
            confirmButton = {
                Button(
                    onClick = onDeleteAccount,
                    enabled = !isLoading,
                    colors = ButtonDefaults.buttonColors(
                        containerColor = MaterialTheme.colorScheme.error
                    )
                ) {
                    Text(stringResource(Res.string.delete_account_confirm))
                }
            },
            dismissButton = {
                TextButton(
                    onClick = { showDeleteAccountDialog = false },
                    enabled = !isLoading
                ) {
                    Text(stringResource(Res.string.cancel))
                }
            }
        )
    }
}

/**
 * Zone des tables actives, à placer dans un conteneur centré. Un échec de chargement est affiché
 * avec un bouton Réessayer : sinon il passerait pour « aucune table active ».
 */
@Composable
private fun ActiveTablesContent(
    activeTables: List<GameTableInfo>,
    isLoadingTables: Boolean,
    tablesError: String?,
    onRetryTables: () -> Unit,
    onTableClick: (String, String) -> Unit
) {
    when {
        isLoadingTables -> CircularProgressIndicator(
            modifier = Modifier.testTag("activeTablesLoading")
        )

        tablesError != null -> ErrorState(
            message = tablesError,
            title = stringResource(Res.string.error_loading_tables),
            onRetry = onRetryTables
        )

        activeTables.isNotEmpty() -> ActiveTablesSection(
            activeTables = activeTables,
            onTableClick = onTableClick,
            modifier = Modifier.fillMaxSize(),
            expandToFill = true
        )

        else -> Text(
            text = stringResource(Res.string.active_tables_empty),
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
            modifier = Modifier.testTag("activeTablesEmpty")
        )
    }
}

@Composable
private fun PortraitLayout(
    characterCount: Int,
    activeTables: List<GameTableInfo>,
    isLoadingTables: Boolean,
    tablesError: String?,
    onRetryTables: () -> Unit,
    onNavigateToCharacters: () -> Unit,
    onCreateCharacter: () -> Unit,
    onNavigateToUniverses: () -> Unit,
    onCreateGame: () -> Unit,
    onJoinGame: (code: String) -> Unit,
    joinError: String?,
    isJoining: Boolean,
    onTableClick: (String, String) -> Unit
) {
    Column(
        modifier = Modifier
            .fillMaxSize()
            .imePadding()
    ) {
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .weight(1f)
                .padding(horizontal = 16.dp)
                .padding(top = 16.dp),
            contentAlignment = Alignment.Center
        ) {
            ActiveTablesContent(
                activeTables = activeTables,
                isLoadingTables = isLoadingTables,
                tablesError = tablesError,
                onRetryTables = onRetryTables,
                onTableClick = onTableClick
            )
        }

        HorizontalDivider()

        Column(
            modifier = Modifier
                .fillMaxWidth()
                .verticalScroll(rememberScrollState())
                .padding(16.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            CharacterManagementSection(
                characterCount = characterCount,
                onNavigateToCharacters = onNavigateToCharacters,
                onCreateCharacter = onCreateCharacter,
                onNavigateToUniverses = onNavigateToUniverses,
                modifier = Modifier.fillMaxWidth()
            )

            GameActionsSection(
                onCreateGame = onCreateGame,
                onJoinGame = onJoinGame,
                joinError = joinError,
                isJoining = isJoining,
                modifier = Modifier.fillMaxWidth()
            )
        }
    }
}

@Composable
private fun LandscapeLayout(
    characterCount: Int,
    activeTables: List<GameTableInfo>,
    isLoadingTables: Boolean,
    tablesError: String?,
    onRetryTables: () -> Unit,
    onNavigateToCharacters: () -> Unit,
    onCreateCharacter: () -> Unit,
    onNavigateToUniverses: () -> Unit,
    onCreateGame: () -> Unit,
    onJoinGame: (code: String) -> Unit,
    joinError: String?,
    isJoining: Boolean,
    onTableClick: (String, String) -> Unit
) {
    val horizontalSpacing = 32.dp
    val verticalSpacing = 16.dp
    Column(
        modifier = Modifier
            .fillMaxSize()
            .padding(horizontal = horizontalSpacing)
            .padding(vertical = verticalSpacing),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(verticalSpacing)
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .weight(1f),
            horizontalArrangement = Arrangement.spacedBy(horizontalSpacing)
        ) {
            Box(
                modifier = Modifier
                    .weight(1f)
                    .fillMaxHeight(),
                contentAlignment = Alignment.Center
            ) {
                ActiveTablesContent(
                    activeTables = activeTables,
                    isLoadingTables = isLoadingTables,
                    tablesError = tablesError,
                    onRetryTables = onRetryTables,
                    onTableClick = onTableClick
                )
            }

            Column(
                modifier = Modifier
                    .weight(1f)
                    .fillMaxHeight()
                    .imePadding()
                    .verticalScroll(rememberScrollState()),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.spacedBy(verticalSpacing)
            ) {
                CharacterManagementSection(
                    characterCount = characterCount,
                    onNavigateToCharacters = onNavigateToCharacters,
                    onCreateCharacter = onCreateCharacter,
                    onNavigateToUniverses = onNavigateToUniverses,
                    modifier = Modifier.fillMaxWidth()
                )

                GameActionsSection(
                    onCreateGame = onCreateGame,
                    onJoinGame = onJoinGame,
                    joinError = joinError,
                    isJoining = isJoining,
                    modifier = Modifier.fillMaxWidth()
                )
            }
        }
    }
}

@Composable
private fun CharacterManagementSection(
    characterCount: Int,
    onNavigateToCharacters: () -> Unit,
    onCreateCharacter: () -> Unit,
    onNavigateToUniverses: () -> Unit,
    modifier: Modifier = Modifier
) {
    Column(
        modifier = modifier,
        verticalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(8.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Button(
                onClick = onNavigateToCharacters,
                enabled = characterCount > 0,
                modifier = Modifier
                    .weight(1f)
                    .height(56.dp)
                    .testTag("characterCountButton"),
                colors = ButtonDefaults.buttonColors(
                    containerColor = if (characterCount > 0) {
                        MaterialTheme.colorScheme.primary
                    } else {
                        MaterialTheme.colorScheme.secondary
                    }
                )
            ) {
                Icon(
                    imageVector = Icons.Default.People,
                    contentDescription = null
                )
                Spacer(modifier = Modifier.width(8.dp))
                Text(
                    text = stringResource(Res.string.character_count, characterCount),
                    style = MaterialTheme.typography.titleMedium,
                    modifier = Modifier.testTag("characterCountText")
                )
            }

            FilledIconButton(
                onClick = onCreateCharacter,
                modifier = Modifier.testTag("createCharacterButton"),
                colors = IconButtonDefaults.filledIconButtonColors(MaterialTheme.colorScheme.primaryContainer)
            ) {
                Icon(
                    imageVector = Icons.Default.Add,
                    contentDescription = stringResource(Res.string.create_character),
                    tint = MaterialTheme.colorScheme.onPrimaryContainer
                )
            }
        }

        Button(
            onClick = onNavigateToUniverses,
            modifier = Modifier
                .fillMaxWidth()
                .height(56.dp)
                .testTag("myUniversesButton"),
            colors = ButtonDefaults.buttonColors(
                containerColor = MaterialTheme.colorScheme.secondaryContainer,
                contentColor = MaterialTheme.colorScheme.onSecondaryContainer
            )
        ) {
            Icon(
                imageVector = Icons.Default.AutoAwesome,
                contentDescription = null
            )
            Spacer(modifier = Modifier.width(8.dp))
            Text(
                text = stringResource(Res.string.my_universes),
                style = MaterialTheme.typography.titleMedium
            )
        }
    }
}

@Composable
private fun ActiveTablesSection(
    activeTables: List<GameTableInfo>,
    onTableClick: (String, String) -> Unit,
    modifier: Modifier = Modifier,
    expandToFill: Boolean = false
) {
    Column(
        modifier = modifier,
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        Text(
            text = stringResource(Res.string.active_tables),
            style = MaterialTheme.typography.titleMedium,
            color = MaterialTheme.colorScheme.onBackground,
            modifier = Modifier.testTag("activeTablesTitle")
        )

        LazyColumn(
            modifier = Modifier
                .fillMaxWidth()
                .then(
                    if (expandToFill) Modifier.weight(1f)
                    else Modifier.heightIn(max = 200.dp)
                )
                .testTag("activeTablesList"),
            verticalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            items(activeTables, key = { it.table.id }) { tableInfo ->
                ActiveTableCard(
                    table = tableInfo.table,
                    role = tableInfo.role,
                    onClick = {
                        onTableClick(
                            tableInfo.table.id,
                            tableInfo.table.universeId
                        )
                    }
                )
            }
        }
    }
}

@Composable
private fun GameActionsSection(
    onCreateGame: () -> Unit,
    onJoinGame: (code: String) -> Unit,
    joinError: String?,
    isJoining: Boolean,
    modifier: Modifier = Modifier
) {
    var joinCode by remember { mutableStateOf(TextFieldValue("")) }

    Column(
        modifier = modifier,
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        Button(
            onClick = onCreateGame,
            modifier = Modifier
                .fillMaxWidth()
                .height(56.dp)
                .testTag("createGameButton")
        ) {
            Text(stringResource(Res.string.create_table))
        }

        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            OutlinedTextField(
                value = joinCode,
                onValueChange = { newValue ->
                    val sanitized = newValue.text
                        .filter { it.isLetterOrDigit() }
                        .take(6)
                        .uppercase()
                    joinCode = if (sanitized == newValue.text) {
                        newValue
                    } else {
                        TextFieldValue(
                            text = sanitized,
                            selection = TextRange(sanitized.length)
                        )
                    }
                },
                label = { Text(stringResource(Res.string.table_code)) },
                placeholder = { Text("ABC123") },
                modifier = Modifier.weight(1f),
                singleLine = true,
                isError = joinError != null,
                enabled = !isJoining,
                keyboardOptions = KeyboardOptions(
                    autoCorrectEnabled = false,
                    capitalization = KeyboardCapitalization.None,
                    keyboardType = KeyboardType.Ascii,
                    imeAction = ImeAction.Done
                ),
                keyboardActions = KeyboardActions(
                    onDone = {
                        if (!isJoining && joinCode.text.length == 6) {
                            onJoinGame(joinCode.text)
                        }
                    }
                ),
                supportingText = { Text("${joinCode.text.length}/6") }
            )

            Button(
                onClick = { onJoinGame(joinCode.text) },
                enabled = !isJoining && joinCode.text.length == 6,
                modifier = Modifier
                    .height(56.dp)
                    .testTag("joinGameButton")
            ) {
                Text(stringResource(Res.string.join_table))
            }
        }

        if (joinError != null) {
            Text(
                text = joinError,
                color = MaterialTheme.colorScheme.error,
                style = MaterialTheme.typography.bodySmall,
                modifier = Modifier.fillMaxWidth()
            )
        }

        if (isJoining) {
            LinearProgressIndicator(modifier = Modifier.fillMaxWidth())
        }
    }
}
